import React, { useState } from 'react';
import { formatDate } from '../utils/dateFormatter';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Sparkles } from "lucide-react";
import ContextDrawer from "../agents/ContextDrawer";
import LegacySearchResults from "./LegacySearchResults";

interface Message {
  id: string;
  role: string;
  content: string;
  type?: string;
  created_at: string;
  human_agent_id?: string;
  message_metadata?: any;
  tool_args?: any;
}

interface MessageDetailsModalProps {
  message: Message;
  isOpen: boolean;
  onClose: () => void;
}

const MessageDetailsModal: React.FC<MessageDetailsModalProps> = ({
  message,
  isOpen,
  onClose,
}) => {
  const [contextOpen, setContextOpen] = useState(false);
  const hasContextSnapshot = !!message.message_metadata?.context_snapshot_id;

  const renderJsonData = (data: any, title: string) => {
    if (!data) return null;

    return (
      <div className="space-y-2">
        <div className="text-sm font-medium">{title}</div>
        <pre className="text-xs bg-muted p-3 rounded-lg overflow-auto max-h-96">
          {JSON.stringify(data, null, 2)}
        </pre>
      </div>
    );
  };

  const renderTextField = (value: string | undefined, title: string) => {
    if (!value) return null;

    return (
      <div className="space-y-2">
        <div className="text-sm font-medium">{title}</div>
        <div className="text-sm bg-muted p-3 rounded-lg whitespace-pre-wrap">
          {value}
        </div>
      </div>
    );
  };

  const metadata = message.message_metadata || {};
  const extractedToolCalls = Array.isArray(metadata.tool_calls)
    ? metadata.tool_calls
    : (Array.isArray(metadata.function_calls) ? metadata.function_calls : null);
  const extractedToolOutput =
    metadata.output ??
    metadata.result ??
    metadata.tool_output ??
    null;
  const detailsDefaultTab =
    message.message_metadata ? "metadata"
      : message.tool_args ? "tool_args"
      : extractedToolCalls ? "tool_calls"
      : "tool_output";

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between gap-2 pr-6">
            <DialogTitle>Message Details</DialogTitle>
            {hasContextSnapshot && (
              <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setContextOpen(true)}>
                <Sparkles className="h-3.5 w-3.5" />
                Show full context
              </Button>
            )}
          </div>
        </DialogHeader>

        {hasContextSnapshot && (
          <ContextDrawer
            messageId={message.id}
            open={contextOpen}
            onClose={() => setContextOpen(false)}
            isAdmin
          />
        )}

        <div className="space-y-6">
          {/* Message Metadata */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Message Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-sm font-medium text-muted-foreground">Message ID</div>
                <div className="text-sm font-mono">{message.id}</div>
              </div>
              <div>
                <div className="text-sm font-medium text-muted-foreground">Role</div>
                <Badge variant={message.role === 'user' ? 'default' : 'secondary'}>
                  {message.role}
                </Badge>
              </div>
              <div>
                <div className="text-sm font-medium text-muted-foreground">Type</div>
                <div className="text-sm">{message.type || 'N/A'}</div>
              </div>
              <div>
                <div className="text-sm font-medium text-muted-foreground">Created At</div>
                <div className="text-sm">{formatDate(message.created_at)}</div>
              </div>
            </div>
          </div>

          <Separator />

          {/* Message Content */}
          <div className="space-y-2">
            <div className="text-sm font-medium">Content</div>
            <div className="text-sm bg-muted p-4 rounded-lg whitespace-pre-wrap">
              {message.content}
            </div>
          </div>

          {/* Message Metadata & Tool Args */}
          {(message.message_metadata || message.tool_args || extractedToolCalls || extractedToolOutput) && (
            <>
              <Separator />
              <Tabs defaultValue={detailsDefaultTab} className="w-full">
                <TabsList>
                  {message.message_metadata && <TabsTrigger value="metadata">Metadata</TabsTrigger>}
                  {message.tool_args && <TabsTrigger value="tool_args">Tool Args</TabsTrigger>}
                  {extractedToolCalls && <TabsTrigger value="tool_calls">Tool Calls</TabsTrigger>}
                  {extractedToolOutput && <TabsTrigger value="tool_output">Tool Output</TabsTrigger>}
                </TabsList>
                {message.message_metadata && (
                  <TabsContent value="metadata">
                    {renderJsonData(message.message_metadata, 'Message Metadata')}
                  </TabsContent>
                )}
                {message.tool_args && (
                  <TabsContent value="tool_args">
                    {renderJsonData(message.tool_args, 'Tool Arguments')}
                  </TabsContent>
                )}
                {extractedToolCalls && (
                  <TabsContent value="tool_calls">
                    {renderJsonData(extractedToolCalls, 'Tool Calls')}
                  </TabsContent>
                )}
                {extractedToolOutput && (
                  <TabsContent value="tool_output">
                    {typeof extractedToolOutput === 'string'
                      ? renderTextField(extractedToolOutput, 'Tool Output')
                      : renderJsonData(extractedToolOutput, 'Tool Output')}
                  </TabsContent>
                )}
              </Tabs>
            </>
          )}

          {/* Legacy search results (older sessions only) */}
          <LegacySearchResults messageId={message.id} />
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default MessageDetailsModal;

